/**
 * Estado das capturas da mesa anexadas ao laudo (E36.F4). // ux-neutrality:allow doc-comment
 *
 * Carrega as capturas da análise no mount/troca de tab e expõe as operações // ux-neutrality:allow doc-comment
 * do fluxo: capturar (upload do PNG), renomear, marcar/desmarcar inclusão e // ux-neutrality:allow doc-comment
 * remover. Diferente das anotações (gesto contínuo), capturar é uma ação // ux-neutrality:allow doc-comment
 * pontual — o estado espera o servidor confirmar (sem otimismo), com `busy` // ux-neutrality:allow doc-comment
 * para a UI desabilitar o botão durante o upload. // ux-neutrality:allow doc-comment
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useApiClient } from '@/lib/api';
import {
  createLaudoAttachment,
  deleteLaudoAttachment,
  listLaudoAttachments,
  updateLaudoAttachment,
  type LaudoAttachmentData,
  type LaudoAttachmentUpdateData,
} from '@/lib/laudo_attachments';

export interface UseLaudoAttachmentsResult {
  attachments: LaudoAttachmentData[];
  /** Upload de captura em andamento (desabilita o botão de capturar). */
  busy: boolean;
  /** Última operação de persistência falhou (limpo na próxima que conclui). */
  saveError: boolean;
  capture: (label: string, image: Blob) => Promise<boolean>;
  rename: (id: string, label: string) => Promise<void>;
  setIncluded: (id: string, includeInLaudo: boolean) => Promise<void>;
  remove: (id: string) => Promise<void>;
}

export function useLaudoAttachments(caseId: string, analysisId: string): UseLaudoAttachmentsResult {
  const client = useApiClient();
  const [attachments, setAttachments] = useState<LaudoAttachmentData[]>([]);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState(false);
  // Troca de tab no meio de um request em voo: descarta a resposta antiga.
  const generationRef = useRef(0);

  useEffect(() => {
    generationRef.current += 1;
    const generation = generationRef.current;
    setAttachments([]);
    listLaudoAttachments(client, caseId, analysisId)
      .then((records) => {
        if (generationRef.current !== generation) return;
        setAttachments(records);
      })
      .catch(() => {
        // Falha de carga não bloqueia a mesa: o checklist abre vazio e a
        // próxima operação reporta erro de persistência se persistir.
      });
  }, [client, caseId, analysisId]);

  const capture = useCallback(
    async (label: string, image: Blob): Promise<boolean> => {
      setBusy(true);
      try {
        const record = await createLaudoAttachment(client, caseId, analysisId, label, image);
        setSaveError(false);
        setAttachments((current) => [...current, record]);
        return true;
      } catch {
        setSaveError(true);
        return false;
      } finally {
        setBusy(false);
      }
    },
    [client, caseId, analysisId],
  );

  const patch = useCallback(
    async (id: string, payload: LaudoAttachmentUpdateData): Promise<void> => {
      try {
        const record = await updateLaudoAttachment(client, caseId, analysisId, id, payload);
        setSaveError(false);
        setAttachments((current) => current.map((a) => (a.id === id ? record : a)));
      } catch {
        setSaveError(true);
      }
    },
    [client, caseId, analysisId],
  );

  const rename = useCallback(
    (id: string, label: string): Promise<void> => patch(id, { label }),
    [patch],
  );

  const setIncluded = useCallback(
    (id: string, includeInLaudo: boolean): Promise<void> =>
      patch(id, { include_in_laudo: includeInLaudo }),
    [patch],
  );

  const remove = useCallback(
    async (id: string): Promise<void> => {
      try {
        await deleteLaudoAttachment(client, caseId, analysisId, id);
        setSaveError(false);
        setAttachments((current) => current.filter((a) => a.id !== id));
      } catch {
        setSaveError(true);
      }
    },
    [client, caseId, analysisId],
  );

  return { attachments, busy, saveError, capture, rename, setIncluded, remove };
}
